import { useCallback } from 'react';
import { identity } from 'ramda';
import { useI18nSelector } from 'js/store/hooks';

import * as Types from '../types';
import * as helpers from '../helpers';
import * as constants from '../constants';

export const useTranslations = (prefix = ''): Types.ITranslateMethod => {
    const i18n = useI18nSelector(identity);

    return useCallback(
        /**
         * @example:
         * const t = useTranslations();
         * t('playtech.mobile.system.tk_prefix.my.tk');
         * t(['playtech.mobile.system.tk_prefix.my.tk', 'playtech.mobile.system.tk_prefix.my.tk2']);

         * const t = useTranslations('playtech.mobile.system.tk_prefix.');
         * t('my.tk');
         * t(['my.tk', 'my.tk2']);

         * when playtech.mobile.system.tk_prefix.my.tk value is not set
         * const t = useTranslations('playtech.mobile.system.tk_prefix.');
         * t('my.tk', 'default value');

         * when playtech.mobile.system.tk_prefix.my.tk value contains {0} {1} ...
         * const t = useTranslations('playtech.mobile.system.tk_prefix.');
         * t('my.tk', [replaceStr1, replaceStr2]);

         * const t = useTranslations('playtech.mobile.system.tk_prefix.');
         * t('my.tk', [replaceStr1, replaceStr2], 'default value');
         *
         * @param {string|string[]} translationKey Translation Key - whole or end part of it
         * @param {...*} [args] single {string} defaultValue
         *                      or Array<string> of replacers followed by {string} defaultValue
         * @returns {string}
         */
        (translationKey, ...args) => {
            if (Array.isArray(translationKey)) {
                const filteredTranslationKey = translationKey.filter(Boolean);

                for (const tk of filteredTranslationKey) {
                    const tkWithPrefix = tk.startsWith(constants.TK_PREFIX) ? tk : prefix + tk;
                    const tkValue = helpers.getTranslationKeyValue({ translationKey: tkWithPrefix, prefix, args, i18n });

                    if (i18n.showTranslationKeys && tk === filteredTranslationKey[0]) {
                        return tkValue;
                    }

                    if (tkValue !== tkWithPrefix || tk === filteredTranslationKey[filteredTranslationKey.length - 1]) {
                        return tkValue;
                    }
                }
            } else if (translationKey && typeof translationKey === 'string') {
                return helpers.getTranslationKeyValue({ translationKey, prefix, args, i18n });
            }

            return '';
        },
        [i18n, prefix]
    );
};
